/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Lockdown (AGL-1501): the panic button. ONE state shape and ONE resolver
 * across four scopes — platform, org, host, user — with precedence
 * platform > org > host > user.
 *
 * This module is deliberately pure: it normalizes the storage carriers into
 * `LockdownState` and answers "is access locked, and what does the visitor
 * see". Where the state LIVES differs per scope, because two of the four
 * scopes already shipped and re-implementing them beside themselves is the
 * bug class this repo keeps re-learning:
 *
 * - **org** — `orgs/{orgId}.suspendedAt` (AGL-202/210), extended here with
 *   `suspendedReasonCode` / `suspendedMessage` / `suspendedUntilMs`. Every
 *   existing reader of `suspendedAt` keeps working unchanged.
 * - **host** — NEW staff-only `suspendedAt` (+ same extensions) on the host
 *   doc. Deliberately NOT `host.maintenance`: that field is CLIENT-writable
 *   (the customer's own maintenance switch, AGL-131) and must never double
 *   as a staff security control the customer could simply switch off.
 * - **platform** — NEW `lockdowns/platform` doc (Admin-SDK read/write; the
 *   collection is staff-only in rules).
 * - **user** — NEW `lockdowns/user--{uid}` doc carrying reason/notice,
 *   alongside the existing Firebase Auth `disabled` flag + refresh-token
 *   revocation which do the actual keying-out.
 *
 * The un-panic invariant lives in the SERVER verdict helper
 * (libs/tenant/data/admin lockdown.ts): a verified `staff` claim bypasses
 * every scope, always — a platform lockdown must never lock out the staff
 * who can lift it. Nothing in this module may be given a way to override
 * that.
 */

// Leaf imports only. `host-naming` imports nothing at all, so the DOMAIN
// scope's apex check cannot introduce a cycle here.
import { TENANT_APEX } from './host-naming'
import { operatorContactLine } from './operator-identity'
// Also a leaf — `platform-brand` imports nothing — so the same no-cycle
// argument as `host-naming` above covers it. Staff-surface copy must read
// the CONFIGURED brand: a self-host operator cannot edit source to rename
// the product (AGL-2153), and "Aglyn cannot reach the database" is a
// sentence their operators would be shown about their own install.
import { PLATFORM_BRAND_NAME } from './platform-brand'
// The plugin-declared levers (AGL-2940). `plugin-entitlements` reaches only
// the plugin catalog and the permission registry, neither of which imports
// anything, so the leaf argument above still holds.
import {
  listPluginLockdownFeatures,
  type PluginLockdownFeatureDeclaration,
} from '../plugin-manager/plugin-entitlements'

export type LockdownScope =
  | 'platform'
  | 'org'
  | 'host'
  | 'domain'
  | 'user'
  | 'feature'

/**
 * FEATURE scope (AGL-1510): kill one capability platform-wide while
 * everything else keeps serving. The launch set maps one-to-one onto the
 * incident shapes the issue names — bot wave → `signups`, malware report →
 * `uploads`, billing bug → `checkout`, malicious listing →
 * `marketplace-installs`. A plugin that can be switched off in an incident
 * declares its own lever through `registerPluginEntitlements` (AGL-2940):
 * the label, the staff-bypass rule, the visitor notice and the API paths it
 * gates all arrive with the declaration, and the staff checklist, the
 * writer and the dispatcher read the two sets as one.
 *
 * Precedence COMPOSES rather than ranks: a platform lock implies every
 * feature (the feature verdict helpers check platform first), while a
 * feature lock implies nothing about the platform/org/host/user scopes —
 * feature states never enter `resolveLockdown`.
 */
export type CoreLockdownFeatureKey =
  | 'signups'
  | 'uploads'
  | 'checkout'
  | 'marketplace-installs'

/**
 * A lever's wire identity: one of the core four, or a key a plugin
 * declared. A string rather than a union because the set is only complete
 * once every declaring plugin has registered, which no type can know.
 */
export type LockdownFeatureKey = CoreLockdownFeatureKey | (string & {})

/**
 * The un-panic invariant, per feature (AGL-1510). At the PLATFORM scope a
 * verified staff claim bypasses unconditionally — that is unchanged and not
 * negotiable. A FEATURE lock is narrower, so the bypass is granted only
 * where it aids incident response, and withheld where a staff action would
 * be the very thing the lock exists to stop:
 *
 * - `uploads: true` — the uploads lock answers a malware report, and the
 *   staff member responding needs to upload a test asset to verify the fix
 *   before lifting the lock for everyone.
 * - `marketplace-installs: true` — same shape: a malicious listing slipped
 *   review, and reproducing the install is part of investigating it.
 * - `checkout: false` — a checkout lock answers a billing/Stripe bug, and a
 *   staff-created checkout session is still a real charge against a real
 *   card. There is no incident-response step that needs money to move;
 *   verification belongs in Stripe test mode.
 * - `signups: false` — an account being created has no staff claim yet, so
 *   a bypass here could never fire honestly; declaring `false` states that
 *   rather than leaving a bypass that only a misattributed claim could use.
 *
 * A plugin's lever carries its own answer on its declaration.
 *
 * Each notice says what is paused AND what still works — a feature lock's
 * whole point is that everything else keeps serving, and the copy must not
 * let a narrow pause read as a wider outage. The checkout notice in
 * particular must NEVER read as a payment failure: "your card was declined"
 * and "we turned checkout off" are different sentences, and only one of them
 * sends a customer to their bank.
 */
const CORE_LOCKDOWN_FEATURES: readonly PluginLockdownFeatureDeclaration[] = [
  {
    key: 'signups',
    label: 'New signups',
    customerName: 'new signups',
    staffBypass: false,
    notice: {
      title: 'New signups are paused',
      body: 'New signups are temporarily paused. Existing accounts can sign in and work as usual.',
    },
  },
  {
    key: 'uploads',
    label: 'Media uploads',
    customerName: 'media uploads',
    staffBypass: true,
    notice: {
      title: 'Uploads are paused',
      body: 'Media uploads are temporarily disabled while we address an issue. Your existing media and published sites are unaffected.',
    },
  },
  {
    key: 'checkout',
    label: 'Checkout (new subscriptions)',
    // Both doors it closes start a purchase: a plan or add-on, and a paid
    // marketplace install.
    customerName: 'new purchases',
    staffBypass: false,
    notice: {
      title: 'Checkout is temporarily unavailable',
      body: 'Checkout is temporarily unavailable — this is not a payment failure, and your account, subscription, and sites are unaffected. Please try again shortly.',
    },
    // `marketplace/checkout` creates NEW Stripe checkout sessions exactly
    // like the billing route (AGL-1545); it is also the front door of a paid
    // install, so it carries BOTH keys and each keeps its own bypass rule.
    apiPaths: { exact: ['marketplace/checkout'] },
  },
  {
    key: 'marketplace-installs',
    label: 'Marketplace installs',
    customerName: 'marketplace installs',
    staffBypass: true,
    notice: {
      title: 'Marketplace installs are paused',
      body: 'Installing from the marketplace is temporarily disabled. Everything already installed keeps working.',
    },
    // Installs-as-a-class, every artifact kind. `marketplace/update-artifact`
    // re-copies a publisher's version into the org, which is an install by
    // another name and the same vector a malicious listing would ride.
    // Publish/review/report paths map to nothing: a marketplace incident
    // must not stop publishers reporting or staff reviewing.
    apiPaths: {
      exact: ['marketplace/checkout', 'marketplace/install', 'marketplace/update-artifact'],
      prefixes: ['marketplace/install-'],
    },
  },
]

/**
 * Every lever, core first and then the plugin declarations in catalog
 * order — the list the staff checklist renders and the writer validates
 * against. Read live rather than snapshotted: plugin declarations arrive
 * at module scope, which can run after any constant here was evaluated.
 */
export function listLockdownFeatures(): PluginLockdownFeatureDeclaration[] {
  return [...CORE_LOCKDOWN_FEATURES, ...listPluginLockdownFeatures()]
}

/** The keys of {@link listLockdownFeatures}, in the same order. */
export function listLockdownFeatureKeys(): LockdownFeatureKey[] {
  return listLockdownFeatures().map((feature) => feature.key)
}

/** One lever's declaration — core or plugin — or `undefined`. */
export function lockdownFeatureDeclaration(
  key: unknown,
): PluginLockdownFeatureDeclaration | undefined {
  if (typeof key !== 'string') return undefined
  return listLockdownFeatures().find((feature) => feature.key === key)
}

export function isLockdownFeatureKey(
  value: unknown,
): value is LockdownFeatureKey {
  return lockdownFeatureDeclaration(value) !== undefined
}

/** Staff-surface label; the key stays the wire/API identity. */
export function lockdownFeatureLabel(key: LockdownFeatureKey): string {
  return lockdownFeatureDeclaration(key)?.label ?? key
}

/**
 * What a customer's mail calls a lever (`media uploads`), never the staff
 * label. A key nothing declares, or a declaration without a name, reads as
 * "a feature" rather than leak a key or a staff label.
 */
export function lockdownFeatureCustomerName(key: LockdownFeatureKey): string {
  return lockdownFeatureDeclaration(key)?.customerName?.trim() || 'a feature'
}

/**
 * Several levers named together in a customer's mail: `AI assist and AI
 * generation`. One pause of several levers is one notice.
 */
export function lockdownFeaturesCustomerText(keys: readonly LockdownFeatureKey[]): string {
  const names = [...new Set(keys.map(lockdownFeatureCustomerName))]
  return new Intl.ListFormat('en-US', { style: 'long', type: 'conjunction' }).format(names)
}

/**
 * Whether a verified staff claim passes this feature's lock. A key nothing
 * declared answers `false`: an undeclared lever has no bypass argument on
 * record, and the safe reading of "no argument" is "no bypass".
 */
export function lockdownFeatureStaffBypass(key: LockdownFeatureKey): boolean {
  return lockdownFeatureDeclaration(key)?.staffBypass === true
}

/**
 * HOW HARD the lock bites (AGL-1511).
 *
 * - `full` — the shipped AGL-1501 behaviour: nothing serves. Sites 503,
 *   sessions refuse, every API call gets the 423.
 * - `read-only` — reads keep serving, WRITES refuse. The right shape for the
 *   most common maintenance need (a schema migration, a data repair, a
 *   suspected-corruption investigation): the customer's site stays up and
 *   earning, visitors browse normally, and nothing races the repair.
 *
 * ABSENT MEANS `full`. Every lockdown written before this field existed is a
 * full lock, and a lock whose strictness cannot be read must be treated as
 * the stricter one — so `full` is both the default and the fail-safe. The
 * writer stores `mode` only for read-only locks, which keeps every existing
 * carrier document byte-identical and needs no migration.
 */
export type LockdownMode = 'full' | 'read-only'

const LOCKDOWN_MODE_KEYS: Record<LockdownMode, true> = {
  full: true,
  'read-only': true,
}
export const LOCKDOWN_MODES = Object.keys(LOCKDOWN_MODE_KEYS) as LockdownMode[]

export function isLockdownMode(value: unknown): value is LockdownMode {
  return typeof value === 'string' && value in LOCKDOWN_MODE_KEYS
}

/** The mode of a state, with the absent-means-`full` default applied. */
export function lockdownMode(
  state: { mode?: LockdownMode } | null | undefined,
): LockdownMode {
  return state?.mode === 'read-only' ? 'read-only' : 'full'
}

export function isReadOnlyLockdown(
  state: { mode?: LockdownMode } | null | undefined,
): boolean {
  return lockdownMode(state) === 'read-only'
}

/**
 * WHAT HAPPENS WHEN WE CANNOT READ THE LOCK (AGL-1621).
 *
 * Lockdown fails OPEN: if the carrier read throws, the verdict is "not
 * locked". That is the right posture for availability — a Firestore blip
 * must not weld every customer site shut — and the wrong one for a legal or
 * abuse takedown, where the whole point is that the content stops being
 * served and STAYS stopped. A DMCA notice is not satisfied by "we served it
 * because our database was down".
 *
 * So the two needs are split on their own axis rather than either one being
 * bent to fit the other:
 *
 * - `standard` — today's behaviour, unchanged. An unreadable lock is not
 *   enforced. Maintenance windows, billing suspensions, precautionary
 *   holds, incident response: everything whose cost of over-refusing is
 *   higher than its cost of under-refusing.
 * - `takedown` — holds through an infrastructure failure. Legal orders,
 *   abuse/CSAM removals, domain hijack disputes: the cost of serving is
 *   higher than the cost of an outage on that one subject.
 *
 * ## Why this is its own field and not read off `scope` or `reason`
 *
 * NOT `scope`: every one of the six scopes carries both classes. A `host`
 * lock is a maintenance window on Tuesday and a court-ordered removal on
 * Wednesday; the scope says WHAT is locked, never why it must hold.
 *
 * NOT `reason`: `reason` is already spoken for as the VISITOR-FACING
 * classifier — it selects the notice copy in `lockdownNotice`. Deriving
 * fail-closed from `reason === 'security'` would both couple what the
 * public is told to how the system behaves under failure, and be exactly
 * the inference this field exists to avoid: most `security` locks are
 * precautionary holds during an investigation, and those must keep failing
 * open. A lock that becomes fail-closed because of a heuristic is a lock
 * that takes a site down for an unrelated reason.
 *
 * ABSENT MEANS `standard`, and that direction is the safety property. Every
 * lock written before this field existed, every lock an operator did not
 * classify, and every doc whose value this build cannot interpret is
 * fail-OPEN — the behaviour shipped today. Getting this backwards during a
 * Firestore incident is the platform-wide outage the split exists to
 * prevent, so only the exact string `takedown` opts in. Same posture and
 * same storage discipline as `mode` above: the writer stores the field only
 * for takedowns, so every existing carrier document stays byte-identical
 * and no migration is needed.
 */
export type LockdownEnforcement = 'standard' | 'takedown'

const LOCKDOWN_ENFORCEMENT_KEYS: Record<LockdownEnforcement, true> = {
  standard: true,
  takedown: true,
}
export const LOCKDOWN_ENFORCEMENTS = Object.keys(
  LOCKDOWN_ENFORCEMENT_KEYS,
) as LockdownEnforcement[]

export function isLockdownEnforcement(
  value: unknown,
): value is LockdownEnforcement {
  return typeof value === 'string' && value in LOCKDOWN_ENFORCEMENT_KEYS
}

/** Staff-surface labels; the key stays the wire/API identity. */
export const LOCKDOWN_ENFORCEMENT_LABELS: Record<LockdownEnforcement, string> = {
  standard: `Standard — releases if ${PLATFORM_BRAND_NAME} cannot reach the database`,
  takedown: `Takedown — keeps holding if ${PLATFORM_BRAND_NAME} cannot reach the database`,
}

/**
 * The enforcement class of a state, with the absent-means-`standard`
 * default applied. Read through this, never bare — an unrecognised value
 * must land on the fail-open default rather than on a truthiness test that
 * would make any junk string fail closed.
 */
export function lockdownEnforcement(
  state: { enforcement?: LockdownEnforcement } | null | undefined,
): LockdownEnforcement {
  return state?.enforcement === 'takedown' ? 'takedown' : 'standard'
}

/**
 * Is this the class that must survive an unreadable carrier? The ONE
 * predicate the fail-closed path reduces to, so "what is a takedown" has a
 * single definition rather than one per reader.
 */
export function isTakedownLockdown(
  state: { enforcement?: LockdownEnforcement } | null | undefined,
): boolean {
  return lockdownEnforcement(state) === 'takedown'
}

/**
 * What a request is trying to DO, which is the only thing a read-only lock
 * discriminates on.
 *
 * `write` is the default everywhere it is not stated, and that direction is
 * deliberate: an enforcement point that forgot to declare its intent refuses
 * during a migration rather than letting an unaudited write through. The
 * cost of the safe default is an over-refused read; the cost of the unsafe
 * one is the corruption the whole mode exists to prevent.
 */
export type LockdownIntent = 'read' | 'write'

/**
 * HTTP method → intent. The safe-method set from RFC 9110 minus TRACE (which
 * nothing here serves): a method not on this list mutates until proven
 * otherwise.
 *
 * A route whose POST is really a query (`where-used`, `plugin-impact`,
 * validators) states `intent: 'read'` explicitly rather than being guessed at
 * from a path.
 */
export function lockdownIntentForMethod(
  method: string | null | undefined,
): LockdownIntent {
  const normalized = typeof method === 'string' ? method.toUpperCase() : ''
  return normalized === 'GET' || normalized === 'HEAD' || normalized === 'OPTIONS'
    ? 'read'
    : 'write'
}

/**
 * Does this state refuse a request of this intent? The ONE predicate every
 * chokepoint's discrimination reduces to — a full lock refuses everything, a
 * read-only lock refuses writes only.
 *
 * Note what is NOT here: the staff bypass. That lives above every read in the
 * server verdict helper and must not acquire a second, mode-shaped copy
 * — "staff bypass unless…" is how an un-panic invariant stops being one.
 */
export function lockdownBlocks(
  state: { mode?: LockdownMode } | null | undefined,
  intent: LockdownIntent,
): boolean {
  if (!state) return false
  return lockdownMode(state) === 'full' || intent === 'write'
}

export type LockdownReasonCode =
  | 'security'
  /**
   * Phishing, fraud or malicious content (AGL-3420). On an ACCOUNT it is a
   * permanent ban: the account is kept only so its address can never sign up
   * again, and after the lock notice it is sent no mail at all — see
   * {@link isAccountBanLockdownReason}. Everywhere a lock's strictness is
   * read from its reason it is at least as strict as `security`.
   */
  | 'abuse'
  | 'billing'
  | 'maintenance'
  | 'manual'

const LOCKDOWN_REASON_CODE_KEYS: Record<LockdownReasonCode, true> = {
  security: true,
  abuse: true,
  billing: true,
  maintenance: true,
  manual: true,
}
export const LOCKDOWN_REASON_CODES = Object.keys(
  LOCKDOWN_REASON_CODE_KEYS,
) as LockdownReasonCode[]

export function isLockdownReasonCode(
  value: unknown,
): value is LockdownReasonCode {
  return (
    typeof value === 'string' && value in LOCKDOWN_REASON_CODE_KEYS
  )
}

/** Staff-surface labels; the key stays the wire/API identity. */
export const LOCKDOWN_REASON_LABELS: Record<LockdownReasonCode, string> = {
  security: 'Security — investigating a concern',
  abuse: 'Abuse — phishing, fraud or malicious content (permanent ban)',
  billing: 'Billing — unresolved payment',
  maintenance: 'Maintenance',
  manual: 'Manual',
}

/**
 * The reasons that mean "a threat, act now": `security`, and `abuse`, which
 * is a confirmed one. Every rule that is stricter for a security lock — who
 * is signed out, which bytes stop serving, which tokens rotate — reads it
 * through here, so a ban is never the milder of the two.
 */
export function isSecurityClassLockdownReason(reason: unknown): boolean {
  return reason === 'security' || reason === 'abuse'
}

/**
 * A lock with this reason, on an account, is a permanent ban (AGL-3420):
 * after its notice the account is sent nothing, by any sender.
 */
export function isAccountBanLockdownReason(reason: unknown): boolean {
  return reason === 'abuse'
}

/** The one shape every enforcement point consumes. */
export interface LockdownState {
  scope: LockdownScope
  /** Set only when `scope === 'feature'` — which capability is locked. */
  feature?: LockdownFeatureKey
  /**
   * Set only on a feature lock placed for ONE workspace (AGL-2927): the
   * org it pauses the capability for. Absent on the platform-wide feature
   * lock, which implies every workspace.
   */
  orgId?: string
  /** Absent = `full` (AGL-1511). Read through `lockdownMode`, never bare. */
  mode?: LockdownMode
  /**
   * Absent = `standard`, i.e. fail-open (AGL-1621). Read through
   * `lockdownEnforcement`/`isTakedownLockdown`, never bare.
   */
  enforcement?: LockdownEnforcement
  reason: LockdownReasonCode
  /**
   * Visitor/user-facing notice text (bounded at write time). Anything staff
   * types here is SHOWN to locked-out users — internal rationale belongs in
   * the audit row, not this field.
   */
  message?: string
  atMs?: number
  /**
   * Optional expiry (maintenance windows end). Once `untilMs` passes the
   * lockdown is simply inactive — access restores with NO staff action and
   * no write.
   */
  untilMs?: number
  actorUid?: string
}

/**
 * `lockdowns/{id}` — the carrier for the two scopes that had none.
 * Doc ids are scope-encoded so a lookup is a single `get`:
 * `platform` and `user--{uid}`. Plain-number timestamps on purpose: the
 * collection is Admin-SDK-only and converter-free, so a partial write can
 * never run a converter that "defaults" a sibling field away (the
 * withConverter-on-partial-writes bug class).
 */
export const LOCKDOWNS_COLLECTION = 'lockdowns'
export const PLATFORM_LOCKDOWN_DOC_ID = 'platform'
export const userLockdownDocId = (uid: string): string => `user--${uid}`
/** `feature--{key}` — same collection, same rules, same audited writer. */
export const featureLockdownDocId = (feature: LockdownFeatureKey): string =>
  `feature--${feature}`
/**
 * `feature--{key}--org--{orgId}` — the same capability switched off for ONE
 * workspace (AGL-2927). The carrier the staff org page's AI pause writes:
 * a spend stop that touches neither the org's entitlements nor its plan, so
 * lifting it restores exactly what the customer bought. Read only by the
 * doors that carry that feature key and name the org, and never implied by
 * the platform-wide document's absence.
 */
export const orgFeatureLockdownDocId = (
  feature: LockdownFeatureKey,
  orgId: string,
): string => `feature--${feature}--org--${orgId}`

/**
 * `domain--{hostname}` — the DOMAIN scope's carrier (AGL-1513).
 *
 * ## Keyed on the hostname, deliberately not on the host id
 *
 * Every other narrow scope keys on the thing it locks: a user by uid, a host
 * by host id. A domain lock cannot, because the incidents it exists for are
 * exactly the ones where the domain moves. A hijacked or disputed domain gets
 * detached and re-attached — to another site, or to another org — and a lock
 * keyed on `hosts/{id}` would be left guarding whichever site the name has
 * since left. Keying on the NAME means the lock follows the name, survives
 * detach/re-attach, and can be placed on a domain that is not currently
 * attached to anything at all, which is the state a dispute is usually
 * resolved in.
 *
 * The hostname is lowercased and used verbatim; the caller is responsible for
 * having validated it as a hostname, and the writer does. Firestore document
 * ids may contain dots, so `example.com` needs no escaping — and the `domain--`
 * prefix keeps the key space disjoint from `user--`/`feature--` in the shared
 * collection.
 */
export const domainLockdownDocId = (hostname: string): string =>
  `domain--${hostname.trim().toLowerCase()}`

/** A bare hostname: labels of a-z0-9/dash, at least two of them. */
const LOCKABLE_DOMAIN_PATTERN =
  /^(?!-)[a-z0-9-]{1,63}(\.(?!-)[a-z0-9-]{1,63})+$/
/** The DNS limit; also what keeps the document id bounded. */
export const LOCKABLE_DOMAIN_MAX = 253

/**
 * Whether `value` is a name the DOMAIN scope will accept (AGL-1513).
 *
 * Shared by the writer and anything that builds the doc id, so "what is a
 * lockable name" has one definition. Two refusals matter:
 *
 *  - **Not hostname-shaped.** The id is derived from this string, and it
 *    arrives from a staff form; `isDocumentId`-style discipline starts by
 *    not minting a document for junk in the first place.
 *  - **Inside the platform apex.** `{sub}.aglyn.app` is OUR name, and the
 *    tenant resolves that space by `subdomain` and never consults the domain
 *    scope for it — so a lock placed there would write a document that no
 *    reader ever looks at. Refusing is the difference between "you cannot do
 *    that" and a control that silently does nothing, which is the worse of
 *    the two by a distance. Taking a platform subdomain down is the HOST
 *    scope's job.
 */
export function isLockableDomain(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const domain = value.trim().toLowerCase()
  if (!domain || domain.length > LOCKABLE_DOMAIN_MAX) return false
  if (!LOCKABLE_DOMAIN_PATTERN.test(domain)) return false
  return domain !== TENANT_APEX && !domain.endsWith(`.${TENANT_APEX}`)
}

export interface LockdownDoc {
  scope: LockdownScope
  /** Present on `feature--{key}` docs only. */
  feature?: LockdownFeatureKey
  /** Present on `feature--{key}--org--{orgId}` docs only (AGL-2927). */
  orgId?: string
  /** Written only for read-only locks; absent = `full`. */
  mode?: LockdownMode
  /** Written only for takedowns; absent = `standard` (fail open). */
  enforcement?: LockdownEnforcement
  reason: LockdownReasonCode
  message?: string
  atMs?: number
  untilMs?: number
  actorUid?: string
}

/** Staff-typed notice text is user-facing; keep it bounded and plain. */
export const LOCKDOWN_MESSAGE_MAX = 500

/**
 * Tolerant epoch-ms reader: Firestore `Timestamp`, `{ seconds }` JSON, a
 * number of ms, or an ISO string — the org carrier's `suspendedAt` arrives
 * in all of these shapes depending on which cache serialized it.
 */
export function toEpochMs(value: unknown): number | undefined {
  if (value == null) return undefined
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }
  if (typeof value === 'object') {
    const record = value as {
      toMillis?: () => number
      seconds?: number
      _seconds?: number
    }
    if (typeof record.toMillis === 'function') {
      try {
        return record.toMillis()
      } catch {
        return undefined
      }
    }
    const seconds = record.seconds ?? record._seconds
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      return seconds * 1000
    }
  }
  return undefined
}

/**
 * Active now? Expiry passing deactivates without any write.
 *
 * Typed on the only field it reads rather than on `LockdownState`, so the
 * sibling levers built on the same field family — asset quarantine
 * (AGL-1512) — share this definition of "expired" instead of restating it.
 * A second copy is how two panic levers end up disagreeing about whether a
 * window that closed one millisecond ago is still in force.
 */
export function isLockdownActive(
  state: { untilMs?: number } | null | undefined,
  nowMs: number,
): boolean {
  if (!state) return false
  if (typeof state.untilMs === 'number' && state.untilMs <= nowMs) return false
  return true
}

/**
 * ACCOUNT CREATION ITSELF (AGL-1531) — the decision a Firebase Auth
 * `beforeUserCreated` blocking function makes.
 *
 * The signups feature lock already refuses the SESSION (the mint in
 * /api/auth/session), the acceptance recorder and the signup-page doors, so
 * a wave's accounts are unusable. They are still CREATED: account creation
 * is client -> Firebase Auth and no Aglyn server sits in front of it. The
 * only thing that does is a blocking function, which is why this decision
 * exists as its own function rather than as another `isLockdownActive` call
 * at a route.
 *
 * ==== EVERYTHING BELOW, TO THE #endregion MARKER, IS COPIED VERBATIM INTO
 * ==== cloud/functions/src/signups-lock.ts.
 *
 * `cloud/functions` is a plain npm package outside the nx workspace — it can
 * import firebase-admin and firebase-functions and nothing else, so it
 * cannot import this library. The copy is therefore byte-for-byte and
 * apps/console/specs/signups-creation-lock-wiring.spec.ts fails if the two
 * regions ever differ by a character. That makes the tests below tests of
 * the code that actually deploys, rather than of a lookalike.
 *
 * The region imports nothing, for that reason.
 */
// #region signups-creation-lock
/**
 * FAIL OPEN WHEN THE LOCK CANNOT BE READ; KEEP HOLDING ONE ALREADY SEEN.
 *
 * This repo's postures are deliberately not uniform — rate limiting fails
 * soft, CSRF fails closed, and `getFeatureLockdown` fails OPEN because an
 * unreachable Firestore is an outage rather than a feature lockdown. This
 * gate sits with the last of those, and the split below is what makes that
 * safe rather than merely convenient.
 *
 * ## Not knowing is answered by admitting
 *
 * An unreadable lock means the platform does not KNOW whether staff pulled
 * the lever. Refusing on "do not know" is not a cautious answer here, it is
 * a total one: this is the only thing standing in front of Firebase Auth
 * account creation, so one refusal turns away every stranger at once, with a
 * generic error they cannot act on and no reason to come back. And the
 * condition that produces it is ordinary rather than exceptional — where
 * signup traffic is light the instance is cold for nearly every attempt, so
 * "the first Firestore read costs more than the budget" is the common case,
 * not the rare one.
 *
 * The error in the other direction is bounded. An account created while
 * Firestore is unreadable cannot finish signing up anyway — the profile, the
 * legal-acceptance record and the workspace all live in Firestore — so
 * admitting costs some orphan Auth records for the length of the read
 * outage, and those are enumerable and removable afterwards. One side's
 * mistake is a handful of empty records; the other side's is the whole
 * funnel, silently, for as long as reads are slow.
 *
 * ## A lever actually seen is a fact, and it survives
 *
 * Every read that COMPLETES is recorded below, and a read that then fails is
 * answered from that record instead of from nothing. So the objection
 * fail-open usually earns — a bot wave makes reads fail and thereby releases
 * the brake aimed at the wave — needs the wave to land on an instance that
 * has never once read the lock; every instance that has keeps refusing for
 * the whole outage. Lifting the lever is itself a Firestore write, so
 * nothing can lift it during that outage either, and a lock's own `untilMs`
 * is still honored against the clock, so a dead-man expiry cannot become
 * un-liftable by being remembered.
 *
 * This is STRICTER than the tenant takedown ledger (AGL-1621), which
 * remembers only locks classified `takedown` and lets a `standard` one
 * release during an outage. That trade is right where it sits, because
 * holding a remembered lock there keeps every visitor off a customer's whole
 * site. Holding one here refuses new signups, which is the cheap direction,
 * so no `enforcement` field is consulted at all and any active lock is
 * remembered.
 *
 * ## What it does not do, stated rather than implied
 *
 * An instance that has never completed a read has nothing to remember, so a
 * lever pulled DURING a total Firestore outage does not reach a cold one.
 * Closing that gap needs a carrier more available than Firestore, which is a
 * different and much larger change; pretending otherwise here would be worse
 * than naming it. The escape hatch that needs no deploy is unchanged:
 * unregister the `beforeCreate` trigger in Identity Platform, which the
 * staff lockdown page reports the state of.
 */
export type SignupsCreationVerdict =
  /**
   * `unreadable` marks an admission made BLIND: the read did not complete
   * and nothing this instance remembers said the lever was pulled. Carried
   * so the caller can log it — the two admissions are the same outcome for
   * the person signing up and a very different one for an operator.
   */
  | { refused: false; unreadable?: true }
  /**
   * `locked` = a read completed and found the lever pulled. `held` = a read
   * failed and an earlier one had found it pulled.
   */
  | { refused: true; cause: 'locked' | 'held' }

/**
 * How long the blocking function waits on the lock read before deciding
 * without it.
 *
 * Blocking functions sit on the account-creation critical path and Identity
 * Platform gives them a bounded window, so an unbounded `get()` would hand
 * the decision to the platform's own timeout — which refuses, after burning
 * the whole budget and with no log saying why. Bounding it keeps the verdict
 * here, where it can be reasoned about and recorded.
 */
export const SIGNUPS_CREATION_LOCK_READ_TIMEOUT_MS = 2_500

/**
 * What the last COMPLETED read saw, for the life of this instance.
 * `undefined` means no read has completed here yet — the state a cold
 * instance starts in, and the only one that cannot hold a lock.
 *
 * Not a cache: it is never consulted in place of a read, only in place of
 * one that failed, so it can never make this gate slower to notice a lift
 * than the lift's own write.
 */
let lastReadSignupsLock: { untilMs?: number } | null | undefined

/**
 * Forget it. Not part of any panic path — this exists for process
 * boundaries and for tests, where one case's lock would otherwise become the
 * next case's refusal.
 */
export function resetSignupsLockMemory(): void {
  lastReadSignupsLock = undefined
}

/**
 * Is this state an engaged lock at `nowMs`?
 *
 * Deliberately stricter than `normalizeLockdownDoc`, which refuses to
 * interpret a malformed doc and so reports NOT LOCKED. Here the document's
 * existence is the lever: the only writer is the audited staff route, so a
 * doc that exists at all means someone pulled it, and a field this build
 * cannot parse must never un-pull it. An expiry that has passed deactivates
 * with no write, matching `isLockdownActive` exactly (the sibling test pins
 * that equivalence).
 */
function signupsLockEngaged(
  state: { untilMs?: number } | null | undefined,
  nowMs: number,
): boolean {
  if (state === null || state === undefined) return false
  return !(typeof state.untilMs === 'number' && state.untilMs <= nowMs)
}

/**
 * Refuse this account creation?
 *
 * `readLock` returns the `lockdowns/feature--signups` document, or null when
 * it does not exist. It is injected rather than imported so this decision —
 * including its fail-open and timeout behavior — is testable without a
 * Firestore, and so the same characters run in the function and in the test.
 *
 * NOT parameterized by provider, by email, or by tenant. Email/password,
 * Google and SSO all reach Firebase Auth account creation, and a lock that
 * discriminated between them would be a lock on one gate of three. The
 * caller passes no identity at all, so no future edit can quietly add a
 * carve-out here.
 */
export async function signupsCreationVerdict(
  readLock: () => Promise<{ untilMs?: number } | null | undefined>,
  nowMs: number,
  timeoutMs: number = SIGNUPS_CREATION_LOCK_READ_TIMEOUT_MS,
): Promise<SignupsCreationVerdict> {
  let timer: ReturnType<typeof setTimeout> | undefined
  let state: { untilMs?: number } | null | undefined
  try {
    state = await Promise.race([
      readLock(),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
          () => reject(new Error('signups lock read timed out')),
          timeoutMs,
        )
      }),
    ])
  } catch {
    // Nothing was learned, so the only thing that can refuse now is what an
    // earlier read established. A remembered lock whose window has closed is
    // dropped here rather than re-examined on every later failure.
    if (signupsLockEngaged(lastReadSignupsLock, nowMs)) {
      return { refused: true, cause: 'held' }
    }
    lastReadSignupsLock = undefined
    return { refused: false, unreadable: true }
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
  // A completed read is the whole truth, "no document" included: it decides
  // this account AND replaces whatever was remembered, which is how a lift
  // takes effect.
  lastReadSignupsLock = state ?? null
  return signupsLockEngaged(state, nowMs)
    ? { refused: true, cause: 'locked' }
    : { refused: false }
}

// #endregion signups-creation-lock

/**
 * Precedence: platform > org > host > domain > user. The widest active scope wins so
 * the notice a visitor sees names the real cause (a platform maintenance
 * window should not read as "this account is suspended").
 *
 * STRICTNESS OUTRANKS WIDTH (AGL-1511). Once locks have a mode, "widest
 * wins" alone is a security hole: a platform-wide read-only maintenance
 * window would outrank — and therefore SOFTEN — a full security takedown on
 * one org, quietly readmitting every visitor to the site staff just took
 * down. So an active `full` lock is chosen first (in scope order among the
 * full ones), and the widest read-only lock only wins when no full lock is
 * active at all. A lock can never be relaxed by a wider, gentler one.
 */
export function resolveLockdown(
  states: {
    platform?: LockdownState | null
    org?: LockdownState | null
    host?: LockdownState | null
    /**
     * AGL-1513. Narrower than `host`: it locks ONE attached name while the
     * same site keeps serving on its other addresses, so it must never
     * outrank a host takedown. It sits above `user` for the same reason
     * `host` does — it is a property of the site being addressed, not of
     * whoever is looking at it.
     */
    domain?: LockdownState | null
    user?: LockdownState | null
  },
  nowMs: number,
): LockdownState | null {
  const active = [
    states.platform,
    states.org,
    states.host,
    states.domain,
    states.user,
  ].filter(
    (state): state is LockdownState => Boolean(state) && isLockdownActive(state, nowMs),
  )
  return active.find((state) => lockdownMode(state) === 'full') ?? active[0] ?? null
}

/**
 * Org carrier → state. `suspendedAt` alone (every pre-lockdown suspension)
 * normalizes to `manual` with no public message — the legacy free-text
 * `suspendedReason` was written for staff eyes and must not leak into the
 * visitor notice.
 */
export function normalizeOrgLockdown(
  org:
    | {
        suspendedAt?: unknown
        suspendedReasonCode?: unknown
        suspendedMessage?: unknown
        suspendedUntilMs?: unknown
        suspendedMode?: unknown
        suspendedEnforcement?: unknown
      }
    | null
    | undefined,
): LockdownState | null {
  if (!org || org.suspendedAt == null) return null
  return {
    scope: 'org',
    // An unrecognised value normalizes to `full`, matching the absent case:
    // a strictness this build cannot interpret must not read as the softer
    // one (an older deploy meeting a mode it has never heard of).
    ...(org.suspendedMode === 'read-only' ? { mode: 'read-only' as const } : {}),
    // Only the exact string opts into fail-closed; anything else — absent,
    // malformed, or a class a newer deploy invented — stays `standard` and
    // keeps failing open (AGL-1621).
    ...(org.suspendedEnforcement === 'takedown'
      ? { enforcement: 'takedown' as const }
      : {}),
    reason: isLockdownReasonCode(org.suspendedReasonCode)
      ? org.suspendedReasonCode
      : 'manual',
    message:
      typeof org.suspendedMessage === 'string' && org.suspendedMessage
        ? org.suspendedMessage.slice(0, LOCKDOWN_MESSAGE_MAX)
        : undefined,
    atMs: toEpochMs(org.suspendedAt),
    untilMs:
      typeof org.suspendedUntilMs === 'number' &&
      Number.isFinite(org.suspendedUntilMs)
        ? org.suspendedUntilMs
        : undefined,
  }
}

/**
 * Host carrier → state. Same field family as the org, on the host doc.
 * `host.maintenance` is NOT consulted here — that is the customer's own
 * switch and keeps its shipped AGL-131 path.
 */
export function normalizeHostLockdown(
  host:
    | {
        suspendedAt?: unknown
        suspendedReasonCode?: unknown
        suspendedMessage?: unknown
        suspendedUntilMs?: unknown
        suspendedMode?: unknown
        suspendedEnforcement?: unknown
      }
    | null
    | undefined,
): LockdownState | null {
  if (!host || host.suspendedAt == null) return null
  return {
    scope: 'host',
    ...(host.suspendedMode === 'read-only' ? { mode: 'read-only' as const } : {}),
    // As the org carrier: exact string only, everything else fails open.
    ...(host.suspendedEnforcement === 'takedown'
      ? { enforcement: 'takedown' as const }
      : {}),
    reason: isLockdownReasonCode(host.suspendedReasonCode)
      ? host.suspendedReasonCode
      : 'manual',
    message:
      typeof host.suspendedMessage === 'string' && host.suspendedMessage
        ? host.suspendedMessage.slice(0, LOCKDOWN_MESSAGE_MAX)
        : undefined,
    atMs: toEpochMs(host.suspendedAt),
    untilMs:
      typeof host.suspendedUntilMs === 'number' &&
      Number.isFinite(host.suspendedUntilMs)
        ? host.suspendedUntilMs
        : undefined,
  }
}

/**
 * The active org/host lock on one site, from documents the caller already
 * holds, or null (AGL-3356).
 *
 * For a sender deep in a plugin — the campaign core, the workflow engine,
 * the outreach runtime — that has the org and host documents in hand and
 * must refuse to mail for a suspended workspace. The server verdict
 * (`getSiteLockdown`) adds the platform scope and the takedown ledger on
 * top; this is the pure part both share, so neither restates what an
 * org/host lock is. Unlike the verdict it reads nothing and cannot fail, so
 * a caller that could not read the documents has already failed on its own.
 *
 * Returns the state whatever its mode; a caller that lets a read-only
 * maintenance window through asks `lockdownMode(state)`.
 */
export function siteLockdownFromDocs(
  docs: {
    org?: Parameters<typeof normalizeOrgLockdown>[0]
    host?: Parameters<typeof normalizeHostLockdown>[0]
  },
  nowMs: number,
): LockdownState | null {
  const state = resolveLockdown(
    {
      org: normalizeOrgLockdown(docs.org),
      host: normalizeHostLockdown(docs.host),
    },
    nowMs,
  )
  return isLockdownActive(state, nowMs) ? state : null
}

/** `lockdowns/{id}` doc → state; refuses malformed docs rather than guess. */
export function normalizeLockdownDoc(
  doc: Partial<LockdownDoc> | null | undefined,
  scope: LockdownScope,
): LockdownState | null {
  if (!doc) return null
  if (!isLockdownReasonCode(doc.reason)) return null
  // A feature doc whose key is not (or no longer) in the enum is refused
  // whole, matching the malformed-reason posture: the panic path does not
  // guess, and an unknown key has no chokepoint to enforce it anyway.
  if (scope === 'feature' && !isLockdownFeatureKey(doc.feature)) return null
  return {
    scope,
    ...(scope === 'feature' && isLockdownFeatureKey(doc.feature)
      ? { feature: doc.feature }
      : {}),
    ...(scope === 'feature' && typeof doc.orgId === 'string' && doc.orgId
      ? { orgId: doc.orgId }
      : {}),
    // Same posture as the org/host carriers: only the exact string relaxes
    // the lock. A malformed or unknown `mode` leaves it full.
    ...(doc.mode === 'read-only' ? { mode: 'read-only' as const } : {}),
    // As the org/host carriers: exact string only. A doc whose enforcement
    // class this build cannot interpret is a STANDARD lock and fails open,
    // never the reverse (AGL-1621).
    ...(doc.enforcement === 'takedown'
      ? { enforcement: 'takedown' as const }
      : {}),
    reason: doc.reason,
    message:
      typeof doc.message === 'string' && doc.message
        ? doc.message.slice(0, LOCKDOWN_MESSAGE_MAX)
        : undefined,
    atMs: typeof doc.atMs === 'number' ? doc.atMs : undefined,
    untilMs:
      typeof doc.untilMs === 'number' && Number.isFinite(doc.untilMs)
        ? doc.untilMs
        : undefined,
    actorUid: typeof doc.actorUid === 'string' ? doc.actorUid : undefined,
  }
}

/**
 * `Retry-After` seconds for a 503, when the lockdown has a known end.
 * Clamped to at least 60 so a window expiring mid-request cannot emit 0.
 */
export function lockdownRetryAfterSeconds(
  state: LockdownState,
  nowMs: number,
): number | undefined {
  if (typeof state.untilMs !== 'number') return undefined
  return Math.max(60, Math.ceil((state.untilMs - nowMs) / 1000))
}

export interface LockdownNotice {
  title: string
  body: string
  /** Shown as the action line; undefined = no contact line (maintenance). */
  contact?: string
  /**
   * The lock is a decision the account holder may appeal — a `security` or
   * `abuse` lock (AGL-3420) — so the contact line offers the appeal rather
   * than taking questions. Set by reason, never by staff's message, so a
   * custom message cannot remove the way to appeal.
   */
  appeal?: boolean
}

/**
 * The address on the visitor-facing 503, from operator configuration
 * (AGL-2016).
 *
 * Was a `'support@aglyn.com'` literal read by eleven call sites below. A
 * self-hosted site that went into lockdown therefore told *its* visitors to
 * write to **Aglyn** about an account Aglyn has no record of and cannot
 * restore. Unlike the abuse form this is not a legal misroute, but it is the
 * same class of wrong answer: the only party who can lift the lock is the
 * operator who set it.
 *
 * Returns `null` when unconfigured, which the notice treats as "no contact
 * line" — the same shape `maintenance` already uses. A lockdown notice with a
 * dangling "contact " and nothing after it would be worse than one that
 * simply does not offer a channel the deployment does not have.
 */
export function lockdownSupportEmail(): string | null {
  return operatorContactLine('support').address
}

/**
 * Per-reason visitor copy — plain and non-alarming. A staff-typed `message`
 * replaces the body; the title and contact line stay per-reason so staff
 * cannot accidentally strip the "how do I get out of this" affordance.
 */
export function lockdownNotice(state: LockdownState): LockdownNotice {
  const custom =
    typeof state.message === 'string' && state.message.trim()
      ? state.message.trim()
      : undefined
  // Feature locks carry feature-specific, honest copy (AGL-1510): what is
  // off, and — just as important — what is NOT affected. Same convention as
  // the per-reason copy below: a staff message replaces the body only.
  if (state.scope === 'feature' && state.feature) {
    return featureLockdownNotice(state.feature, custom, state.untilMs)
  }
  // A read-only lock refused a WRITE, and the full-lock copy would lie about
  // it: "Access is temporarily disabled" is false on a site the reader is
  // currently looking at (AGL-1511).
  if (isReadOnlyLockdown(state)) {
    return readOnlyLockdownNotice(state, custom)
  }
  // DOMAIN scope (AGL-1513). The site is fine and is still serving on its
  // other addresses; this NAME is the thing that is not, and the copy says
  // that much and no more.
  if (state.scope === 'domain') {
    return domainLockdownNotice(state, custom)
  }
  // A SITE lock closes one site, not the account that owns it: its default
  // copy says "this site", or the owners' notice tells them their account was
  // closed when their other sites are serving (AGL-3432).
  const subject = state.scope === 'host' ? 'site' : 'account'
  switch (state.reason) {
    case 'maintenance': {
      const until =
        typeof state.untilMs === 'number'
          ? new Date(state.untilMs).toUTCString()
          : undefined
      return {
        title: 'Down for maintenance',
        body:
          custom ??
          (until
            ? `Scheduled maintenance is in progress. Expected back by ${until}.`
            : 'Scheduled maintenance is in progress. Please check back shortly.'),
      }
    }
    case 'billing':
      return {
        title: 'Account on hold',
        body:
          custom ??
          `This ${subject} is on hold over an unresolved billing issue. ` +
            'Updating the payment method in workspace billing settings ' +
            'restores access.',
        contact: lockdownSupportEmail() ?? undefined,
      }
    case 'security':
      return {
        title: 'Temporarily unavailable',
        body:
          custom ??
          'Access is temporarily disabled while we investigate a security ' +
            'concern.',
        contact: lockdownSupportEmail() ?? undefined,
        // A platform-wide lock is ours, with nobody to appeal it.
        ...(state.scope !== 'platform' ? { appeal: true } : {}),
      }
    case 'abuse':
      return {
        title: 'Unavailable',
        body:
          custom ??
          `This ${subject} has been closed for a violation of our Terms of ` +
            'Service.',
        contact: lockdownSupportEmail() ?? undefined,
        // A platform-wide lock is ours, with nobody to appeal it.
        ...(state.scope !== 'platform' ? { appeal: true } : {}),
      }
    case 'manual':
    default:
      return {
        title: 'Temporarily unavailable',
        body: custom ?? `Access to this ${subject} is currently disabled.`,
        contact: lockdownSupportEmail() ?? undefined,
      }
  }
}

/**
 * DOMAIN copy (AGL-1513), for a visitor who typed one particular name.
 *
 * ## It must never name the address that still works
 *
 * Every other scope's copy can afford to be helpful, because the thing being
 * withheld is the thing the reader is entitled to. This scope exists for
 * ownership disputes and hijacked or expired domains now pointing at us — so
 * the person reading this notice may be precisely the party the site is being
 * withheld from, and "try acme.aglyn.app instead" would lift the lock in one
 * sentence. None of these bodies names the platform subdomain, and none
 * should acquire one; the `*.aglyn.app` address is deliberately absent rather
 * than accidentally missing.
 *
 * ## It also does not say whose the name is
 *
 * A dispute is exactly the case where we do not know, and a notice is a
 * publication. "Not currently serving" is the whole of what we can assert
 * without taking a side in something a registrar or a court settles.
 */
function domainLockdownNotice(
  state: LockdownState,
  custom: string | undefined,
): LockdownNotice {
  const title = 'This address is unavailable'
  const contact = lockdownSupportEmail() ?? undefined
  switch (state.reason) {
    case 'maintenance':
      return {
        title,
        body:
          custom ??
          'This web address is temporarily not serving while maintenance ' +
            'is in progress.',
        contact,
      }
    case 'billing':
      return {
        title,
        body:
          custom ??
          'This web address is not currently serving over an unresolved ' +
            'billing issue.',
        contact,
      }
    case 'security':
      return {
        title,
        body:
          custom ??
          'This web address is not currently serving while we investigate ' +
            'a report about it.',
        contact,
      }
    case 'abuse':
      return {
        title,
        body:
          custom ??
          'This web address is not serving because of a violation of our ' +
            'Terms of Service.',
        contact,
      }
    case 'manual':
    default:
      return {
        title,
        body: custom ?? 'This web address is not currently serving.',
        contact,
      }
  }
}

/**
 * READ-ONLY copy (AGL-1511), for the account holder whose save just refused.
 *
 * The whole point of this mode is that the reader is still looking at their
 * work while being told they cannot change it, so every sentence has to hold
 * both halves at once: nothing is down, nothing is lost, one verb is paused.
 * The full-lock titles ("Temporarily unavailable", "Account on hold") are
 * flatly untrue here and would send someone to support over a fifteen-minute
 * migration.
 *
 * The `until` sentence is built with the SAME `lockdownUntilSuffix` helper
 * the feature copy uses, so `parseLockdownRefusal` strips and re-renders it
 * in the reader's local time here too rather than showing a UTC stamp.
 */
function readOnlyLockdownNotice(
  state: LockdownState,
  custom: string | undefined,
): LockdownNotice {
  const window =
    typeof state.untilMs === 'number'
      ? ` ${lockdownUntilSuffix(state.untilMs)}`
      : ''
  const title = 'Changes are temporarily paused'
  switch (state.reason) {
    case 'maintenance':
      return {
        title,
        body:
          custom ??
          `Saving changes is paused while we complete scheduled maintenance. ` +
            `Your sites keep serving and nothing you have created is ` +
            `affected — changes will save again shortly.${window}`,
      }
    case 'billing':
      return {
        title,
        body:
          custom ??
          `Saving changes is paused over an unresolved billing issue. Your ` +
            `sites keep serving and nothing has been deleted — updating the ` +
            `payment method in workspace billing settings restores ` +
            `editing.${window}`,
        contact: lockdownSupportEmail() ?? undefined,
      }
    case 'security':
    case 'manual':
    default:
      return {
        title,
        body:
          custom ??
          `Saving changes is paused while we work on something. Your sites ` +
            `keep serving and nothing you have created is affected — ` +
            `changes will save again shortly.${window}`,
        contact: lockdownSupportEmail() ?? undefined,
      }
  }
}

/**
 * The surfaces on a customer's LIVE SITE that a read-only lock refuses
 * (AGL-1511). These are the ones whose reader is a visitor, not our
 * customer — someone who has never heard of a workspace, a lockdown or a
 * maintenance window and is simply trying to buy something or send a
 * message.
 */
export type LockdownPausedSurface = 'form' | 'checkout' | 'cart' | 'generic'

/**
 * VISITOR copy for a read-only refusal on a tenant site (AGL-1511).
 *
 * The issue's central product call: a customer's site staying up and earning
 * is the entire reason this mode exists instead of full lockdown, so a
 * visitor-facing write gets a polite inline pause, never a page-level 503.
 * That decision only pays off if the words match it — a visitor shown
 * "Temporarily unavailable" on a page that plainly loaded concludes the shop
 * is broken and leaves, which costs the customer the sale the mode was
 * protecting.
 *
 * So: no mention of maintenance windows, workspaces or accounts (none of
 * which are the visitor's), no support address (support is the SITE
 * owner's, not ours, and pointing a stranger at aglyn.com is worse than
 * silent), an explicit "nothing you typed is lost", and — for checkout — the
 * same hard rule the feature copy carries: this must never read as a
 * declined card. A staff-typed `message` is deliberately NOT honoured here;
 * it is written for the account holder and would land in front of strangers.
 */
export function lockdownPausedNotice(
  surface: LockdownPausedSurface,
): LockdownNotice {
  switch (surface) {
    case 'form':
      return {
        title: 'Temporarily paused',
        body:
          'This form is not accepting submissions for a few minutes. ' +
          'Nothing you typed has been lost — please try again shortly.',
      }
    case 'checkout':
      return {
        title: 'Checkout is temporarily paused',
        body:
          'Checkout is paused for a few minutes — this is not a payment ' +
          'problem and you have not been charged. Your basket is safe; ' +
          'please try again shortly.',
      }
    case 'cart':
      return {
        title: 'Temporarily paused',
        body:
          'Basket changes are paused for a few minutes. Browsing works as ' +
          'normal — please try again shortly.',
      }
    case 'generic':
    default:
      return {
        title: 'Temporarily paused',
        body:
          'This action is paused for a few minutes. Browsing works as ' +
          'normal — please try again shortly.',
      }
  }
}

/**
 * Per-feature visitor copy (AGL-1510), read off the lever's declaration —
 * core or plugin — so a plugin's lever explains itself in its own words. A
 * key nothing declared (a document written before its plugin was removed)
 * gets the generic pause: the lock is still honored, it just cannot name
 * what it pauses.
 */
function featureLockdownNotice(
  feature: LockdownFeatureKey,
  custom: string | undefined,
  untilMs?: number,
): LockdownNotice {
  const window =
    typeof untilMs === 'number'
      ? ` Expected back by ${new Date(untilMs).toUTCString()}.`
      : ''
  const declared = lockdownFeatureDeclaration(feature)
  return {
    title: declared?.notice.title ?? 'Temporarily paused',
    body:
      custom ??
      `${
        declared?.notice.body ??
        'This action is paused for a few minutes. Browsing works as normal — please try again shortly.'
      }${window}`,
    contact: lockdownSupportEmail() ?? undefined,
  }
}

/**
 * The wire shape of the 423 refusal body (`lockdownJsonResponse`). Declared
 * here, beside the copy that fills it, so the client parser below and the
 * server writer agree by construction rather than by comment.
 */
export interface LockdownRefusalBody {
  error?: unknown
  scope?: unknown
  feature?: unknown
  mode?: unknown
  reason?: unknown
  title?: unknown
  message?: unknown
  contact?: unknown
  appeal?: unknown
  untilMs?: unknown
}

/** A 423 body, parsed into something a client surface can render. */
export interface LockdownRefusalNotice {
  title: string
  message: string
  contact?: string
  /** The contact line offers an appeal — see {@link LockdownNotice.appeal}. */
  appeal?: boolean
  scope?: LockdownScope
  feature?: LockdownFeatureKey
  /**
   * `read-only` when the server refused a WRITE but is still serving reads
   * (AGL-1511). Absent on every refusal from a full lock, and on any older
   * deploy — a client must treat absence as "no claim", never as "full".
   */
  mode?: LockdownMode
  untilMs?: number
  /**
   * The expiry as a human, LOCAL-time line — `undefined` when the lock has
   * no expiry, which is most of them. Never a raw epoch number.
   */
  until?: string
}

/**
 * The generic-but-honest fallback: what a client says when the server said
 * Locked but the body told it nothing else. It must still be TRUE — "this is
 * paused", never "something went wrong" (the failure this whole affordance
 * exists to stop), and never the word `undefined`.
 */
export const LOCKDOWN_REFUSAL_FALLBACK_TITLE = 'Temporarily unavailable'
export const LOCKDOWN_REFUSAL_FALLBACK_MESSAGE =
  'This is temporarily paused while we work on something. Nothing you have ' +
  'created is affected — please try again shortly.'

/**
 * The exact sentence the notice builders append to a DEFAULT body when a
 * lock has an expiry. Built here so the client parser can strip it by exact
 * match (same `untilMs`, same string) instead of sniffing a regex.
 */
function lockdownUntilSuffix(untilMs: number): string {
  return `Expected back by ${new Date(untilMs).toUTCString()}.`
}

/**
 * The expiry as a client-side, LOCAL-time line (AGL-1532). A UTC string is
 * correct and unreadable; a customer wants to know when to come back on
 * their own clock.
 */
export function formatLockdownUntil(untilMs: number): string | undefined {
  if (!Number.isFinite(untilMs)) return undefined
  const when = new Date(untilMs)
  if (Number.isNaN(when.getTime())) return undefined
  const stamp = when.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
  return `Expected back around ${stamp}.`
}

/**
 * Parse a fetch response's status + parsed JSON body into a renderable
 * lockdown notice, or `null` when this was not a lockdown refusal
 * (AGL-1532).
 *
 * ONE parser, used by every client call site a feature lock can refuse —
 * billing checkout, marketplace installs and purchases, the AI-assist
 * drawer. Three copies of this parsing is the second-implementation shape
 * that lets one surface drift back to "checkout failed" while the others
 * stay honest.
 *
 * Three rules, each of which a spec pins:
 *
 *  1. **Only 423.** A 500 — a real, unexplained failure — returns `null` so
 *     the caller keeps its generic error toast. Dressing a genuine fault as
 *     a deliberate pause is a worse lie than the one being fixed.
 *  2. **A 423 always yields a notice.** The server said Locked; that is the
 *     honest thing to render even if the body is malformed, truncated by a
 *     proxy, or from an older deploy. Missing fields degrade to the shared
 *     per-feature copy when the body names a known feature, and to the
 *     generic-but-honest fallback otherwise.
 *  3. **No duplicated expiry.** The default server copy already ends with a
 *     UTC "Expected back by …" sentence; that exact suffix is stripped and
 *     restated as `until` in the reader's local time. A staff-typed custom
 *     message never carries the suffix, so nothing is stripped from it.
 */
export function parseLockdownRefusal(
  status: number,
  body: unknown,
): LockdownRefusalNotice | null {
  if (status !== 423) return null
  const payload: LockdownRefusalBody =
    body && typeof body === 'object' ? (body as LockdownRefusalBody) : {}
  const feature = isLockdownFeatureKey(payload.feature)
    ? payload.feature
    : undefined
  const untilMs =
    typeof payload.untilMs === 'number' && Number.isFinite(payload.untilMs)
      ? payload.untilMs
      : undefined
  // A body that named a feature but lost its copy still gets the RIGHT
  // words — the same ones the server would have sent — because that copy
  // lives here too. Only a body naming nothing falls all the way back.
  const derived = feature
    ? featureLockdownNotice(feature, undefined, untilMs)
    : undefined
  const title =
    typeof payload.title === 'string' && payload.title.trim()
      ? payload.title.trim()
      : (derived?.title ?? LOCKDOWN_REFUSAL_FALLBACK_TITLE)
  let message =
    typeof payload.message === 'string' && payload.message.trim()
      ? payload.message.trim()
      : (derived?.body ?? LOCKDOWN_REFUSAL_FALLBACK_MESSAGE)
  if (typeof untilMs === 'number') {
    const suffix = lockdownUntilSuffix(untilMs)
    if (message.endsWith(suffix)) {
      message = message.slice(0, -suffix.length).trim()
    }
  }
  const contact =
    typeof payload.contact === 'string' && payload.contact.trim()
      ? payload.contact.trim()
      : undefined
  const until =
    typeof untilMs === 'number' ? formatLockdownUntil(untilMs) : undefined
  return {
    title,
    message,
    ...(contact ? { contact } : {}),
    ...(contact && payload.appeal === true ? { appeal: true } : {}),
    ...(typeof payload.scope === 'string'
      ? { scope: payload.scope as LockdownScope }
      : {}),
    ...(feature ? { feature } : {}),
    ...(isLockdownMode(payload.mode) ? { mode: payload.mode } : {}),
    ...(typeof untilMs === 'number' ? { untilMs } : {}),
    ...(until ? { until } : {}),
  }
}

/**
 * The notice as ONE line, for surfaces whose only affordance is a snackbar
 * (AGL-1532). The title is prefixed only when the message does not already
 * open with it — the checkout copy leads with its own title, and "Checkout
 * is temporarily unavailable — Checkout is temporarily unavailable — this is
 * not a payment failure" helps nobody.
 */
export function lockdownRefusalText(notice: LockdownRefusalNotice): string {
  const lower = notice.message.toLowerCase()
  const led = lower.startsWith(notice.title.toLowerCase())
  const head = led ? notice.message : `${notice.title} — ${notice.message}`
  return notice.until ? `${head} ${notice.until}` : head
}

/**
 * Which VISITOR surface a tenant plugin-API path belongs to, for read-only
 * pause copy (AGL-1511).
 *
 * The tenant dispatcher refuses every mutating method under a read-only lock
 * regardless of path — this only chooses the WORDS, and it exists because
 * the checkout sentence has a requirement no generic copy can carry: it must
 * never read as a declined card. Anything unrecognised gets the neutral
 * generic pause rather than a guess; being vague at a stranger is cheap,
 * being wrong about their money is not.
 */
export function lockdownPausedSurfaceForPluginApiPath(
  path: string,
): LockdownPausedSurface {
  if (
    path === 'commerce/checkout' ||
    path === 'commerce/cart-checkout' ||
    path === 'commerce/pos-order' ||
    path === 'commerce/pos-payment' ||
    path === 'commerce/draft-order'
  ) {
    return 'checkout'
  }
  if (path === 'commerce/cart' || path.startsWith('commerce/cart/')) return 'cart'
  return 'generic'
}

/**
 * Which feature keys gate a plugin-API dispatcher path (AGL-1510, plural
 * since AGL-1545). Lives here (pure, beside the catalog) so the dispatcher's
 * wiring is one call and the mapping is unit-testable without a route
 * harness.
 *
 * Every lever declares the paths it gates: exact paths, and prefixes matched
 * on a SEGMENT boundary, so `ai/generate` gates `ai/generate/section` and
 * never `ai/generated-report`. A path several levers name is gated by all
 * of them, each keeping its own staff-bypass rule when composed —
 * `marketplace/checkout` carries both `checkout` and `marketplace-installs`
 * (AGL-1545): it creates NEW Stripe checkout sessions exactly like the
 * billing route, and it is also the front door of a paid install, so a
 * malicious-listing incident must stop buyers PAYING for the artifact under
 * investigation, not merely refuse the install after the money moved.
 *
 * A plugin's lever gates its paths by existing (AGL-2903): a door
 * registered under a declared prefix is gated before anyone remembers to
 * wire it, which is the reason the mapping is declared beside the lever
 * rather than beside the route.
 */
export function lockdownFeaturesForPluginApiPath(
  path: string,
): LockdownFeatureKey[] {
  const keys: LockdownFeatureKey[] = []
  for (const feature of listLockdownFeatures()) {
    const exact = feature.apiPaths?.exact ?? []
    const prefixes = feature.apiPaths?.prefixes ?? []
    const hit =
      exact.includes(path) ||
      prefixes.some(
        (prefix) =>
          path === prefix ||
          path.startsWith(prefix.endsWith('/') || prefix.endsWith('-') ? prefix : `${prefix}/`),
      )
    if (hit && !keys.includes(feature.key)) keys.push(feature.key)
  }
  return keys
}
